import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';
import { contractService } from '../../services/contract.service';
import { ContractType } from '../../types';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';

const contractSchema = z.object({
  contractName: z.string().min(1, 'Contract name is required').max(255),
  clientName: z.string().min(1, 'Client name is required'),
  clientContact: z.string().optional(),
  clientReference: z.string().optional(),
  contractType: z.nativeEnum(ContractType),
  description: z.string().max(2000).optional(),
  totalWeightTons: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().positive('Weight must be positive')
  ),
  totalDistanceKm: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().positive().optional()
  ),
  origin: z.string().min(1, 'Origin is required'),
  destination: z.string().min(1, 'Destination is required'),
  contractValue: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().positive('Contract value must be positive')
  ),
  ratePerTon: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().positive().optional()
  ),
  ratePerKm: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().positive().optional()
  ),
  currency: z.string().default('TZS'),
  paymentTerms: z.string().optional(),
  startDate: z.string().min(1, 'Start date is required'),
  expectedCompletionDate: z.string().optional(),
  endDate: z.string().optional(),
  specialConditions: z.string().optional(),
  notes: z.string().optional(),
});

type ContractFormData = z.infer<typeof contractSchema>;

export const CreateContractPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ContractFormData>({
    // @ts-expect-error - Known issue with React Hook Form and Zod coerce types
    resolver: zodResolver(contractSchema),
    defaultValues: {
      contractType: ContractType.REGULAR,
      currency: 'TZS',
      startDate: new Date().toISOString().split('T')[0],
    },
  });

  const contractValue = watch('contractValue');
  const totalWeight = watch('totalWeightTons');
  const ratePerTon = watch('ratePerTon');

  React.useEffect(() => {
    if (contractValue && totalWeight && !ratePerTon) {
      const calculatedRate = contractValue / totalWeight;
      setValue('ratePerTon', Number(calculatedRate.toFixed(2)));
    }
  }, [contractValue, totalWeight, ratePerTon, setValue]);

  const createMutation = useMutation({
    mutationFn: contractService.create,
    onSuccess: (data) => {
      toast.success('Contract created successfully');
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      navigate(`/contracts/${data.id}`);
    },
    onError: () => {
      toast.error('Failed to create contract');
    },
  });

  const onSubmit = (data: ContractFormData) => {
    createMutation.mutate(data);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate('/contracts')}>
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-neutral-charcoal">Create New Contract</h1>
          <p className="text-gray-600">Enter contract details and terms</p>
        </div>
      </div>

      {/* @ts-expect-error - Known issue with React Hook Form and Zod coerce types */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <div className="mb-4">
            <h2 className="text-lg font-semibold">Basic Information</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contract Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('contractName')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="e.g., ABC Mining - 300 Ton Transport"
              />
              {errors.contractName && (
                <p className="text-red-500 text-sm mt-1">{errors.contractName.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contract Type <span className="text-red-500">*</span>
              </label>
              <select
                {...register('contractType')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {Object.values(ContractType).map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                {...register('description')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Describe the contract scope, requirements, and special conditions..."
              />
            </div>
          </div>
        </Card>

        <Card>
          <div className="mb-4">
            <h2 className="text-lg font-semibold">Client Information</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('clientName')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Client company name"
              />
              {errors.clientName && <p className="text-red-500 text-sm mt-1">{errors.clientName.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Contact</label>
              <input
                type="text"
                {...register('clientContact')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="+255 XXX XXX XXX"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Reference/PO Number
              </label>
              <input
                type="text"
                {...register('clientReference')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="e.g., TENDER-2026-001"
              />
            </div>
          </div>
        </Card>

        <Card>
          <div className="mb-4">
            <h2 className="text-lg font-semibold">Scope & Logistics</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Weight (tons) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                {...register('totalWeightTons')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="300"
              />
              {errors.totalWeightTons && (
                <p className="text-red-500 text-sm mt-1">{errors.totalWeightTons.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Distance (km)</label>
              <input
                type="number"
                step="0.1"
                {...register('totalDistanceKm')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="1200"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Origin <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('origin')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="e.g., Dar es Salaam Port"
              />
              {errors.origin && <p className="text-red-500 text-sm mt-1">{errors.origin.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Destination <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('destination')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="e.g., Mwanza Mining Site"
              />
              {errors.destination && <p className="text-red-500 text-sm mt-1">{errors.destination.message}</p>}
            </div>
          </div>
        </Card>

        <Card>
          <div className="mb-4">
            <h2 className="text-lg font-semibold">Financial Terms</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contract Value (TZS) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="1"
                {...register('contractValue')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="75000000"
              />
              {errors.contractValue && (
                <p className="text-red-500 text-sm mt-1">{errors.contractValue.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rate per Ton (TZS)</label>
              <input
                type="number"
                step="0.01"
                {...register('ratePerTon')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Auto-calculated"
              />
              {contractValue && totalWeight && (
                <p className="text-sm text-gray-500 mt-1">
                  Calculated: {formatCurrency(contractValue / totalWeight)} per ton
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rate per KM (TZS)</label>
              <input
                type="number"
                step="0.01"
                {...register('ratePerKm')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="2500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
              <textarea
                {...register('paymentTerms')}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="e.g., Net 30 days after completion, 50% upfront..."
              />
            </div>
          </div>
        </Card>

        <Card>
          <div className="mb-4">
            <h2 className="text-lg font-semibold">Timeline</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                {...register('startDate')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {errors.startDate && <p className="text-red-500 text-sm mt-1">{errors.startDate.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expected Completion Date
              </label>
              <input
                type="date"
                {...register('expectedCompletionDate')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                {...register('endDate')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        </Card>

        <Card>
          <div className="mb-4">
            <h2 className="text-lg font-semibold">Additional Terms</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Special Conditions</label>
              <textarea
                {...register('specialConditions')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Any special requirements, restrictions, or conditions..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Internal Notes</label>
              <textarea
                {...register('notes')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Internal notes about this contract (not visible to client)..."
              />
            </div>
          </div>
        </Card>

        <div className="flex justify-end gap-4">
          <Button type="button" variant="secondary" onClick={() => navigate('/contracts')}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating...' : 'Create Contract'}
          </Button>
        </div>
      </form>
    </div>
  );
};
